import { Suspense, lazy } from 'react';
import type { PartialRouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';
import { AuthGuard } from '../../components/auth-guard';
import { GuestGuard } from '../../components/guest-guard';
import { LoadingScreen } from '../../components/loading-screen';
import { PermissionsGuard } from '../../components/permissions-guard';
import { TenantGuard } from '../../components/tenant-guard';
import { DashboardLayout } from '../../containers/dashboard-layout';
import { Permission } from '../../types/user';
import { Job } from '../../containers/job';

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// Not found pages
const NotFound = Loadable(
  lazy(() =>
    import('../../containers/not-found').then((module) => ({
      default: module.NotFound,
    })),
  ),
);

// Auth pages
const Login = Loadable(
  lazy(() =>
    import('../../containers/login').then((module) => ({
      default: module.Login,
    })),
  ),
);

const Token = Loadable(
  lazy(() =>
    import('../../containers/token').then((module) => ({
      default: module.Token,
    })),
  ),
);

const ErrorPage = Loadable(
  lazy(() =>
    import('../../containers/error-page').then((module) => ({
      default: module.ErrorPage,
    })),
  ),
);

const NoPermissions = Loadable(
  lazy(() =>
    import('../../containers/no-permissions').then((module) => ({
      default: module.NoPermissions,
    })),
  ),
);

// Dashboard pages
const Items = Loadable(
  lazy(() =>
    import('../../containers/items').then((module) => ({
      default: module.Items,
    })),
  ),
);

const Item = Loadable(
  lazy(() =>
    import('../../containers/item').then((module) => ({
      default: module.Item,
    })),
  ),
);

const ItemDetails = Loadable(
  lazy(() =>
    import('../../containers/item-details').then((module) => ({
      default: module.ItemDetails,
    })),
  ),
);

const ItemTimeline = Loadable(
  lazy(() =>
    import('../../containers/item-timeline').then((module) => ({
      default: module.ItemTimeline,
    })),
  ),
);

const ItemJobResult = Loadable(
  lazy(() =>
    import('../../containers/item-job-result').then((module) => ({
      default: module.ItemJobResult,
    })),
  ),
);

const ItemProtect = Loadable(
  lazy(() =>
    import('../../containers/item-protect').then((module) => ({
      default: module.ItemProtect,
    })),
  ),
);

const ItemVerify = Loadable(
  lazy(() =>
    import('../../containers/item-verify').then((module) => ({
      default: module.ItemVerify,
    })),
  ),
);

const SKUs = Loadable(
  lazy(() =>
    import('../../containers/skus').then((module) => ({
      default: module.SKUs,
    })),
  ),
);

const SKUCreate = Loadable(
  lazy(() =>
    import('../../containers/sku-create').then((module) => ({
      default: module.SKUCreate,
    })),
  ),
);

const SKU = Loadable(
  lazy(() =>
    import('../../containers/sku').then((module) => ({
      default: module.SKU,
    })),
  ),
);

const SKUDetails = Loadable(
  lazy(() =>
    import('../../containers/sku-details').then((module) => ({
      default: module.SKUDetails,
    })),
  ),
);

const SKUItems = Loadable(
  lazy(() =>
    import('../../containers/sku-items').then((module) => ({
      default: module.SKUItems,
    })),
  ),
);

const SKUTimeline = Loadable(
  lazy(() =>
    import('../../containers/sku-timeline').then((module) => ({
      default: module.SKUTimeline,
    })),
  ),
);

const SKUItemRegister = Loadable(
  lazy(() =>
    import('../../containers/sku-item-register').then((module) => ({
      default: module.SKUItemRegister,
    })),
  ),
);

const SKUItemIdentify = Loadable(
  lazy(() =>
    import('../../containers/sku-item-identify').then((module) => ({
      default: module.SKUItemIdentify,
    })),
  ),
);

const SKUJobResult = Loadable(
  lazy(() =>
    import('../../containers/sku-job-result').then((module) => ({
      default: module.SKUJobResult,
    })),
  ),
);

const StatisticsGlobal = Loadable(
  lazy(() =>
    import('../../containers/statistics-global').then((module) => ({
      default: module.StatisticsGlobal,
    })),
  ),
);

const Tenants = Loadable(
  lazy(() =>
    import('../../containers/tenants').then((module) => ({
      default: module.Tenants,
    })),
  ),
);

const TenantCreate = Loadable(
  lazy(() =>
    import('../../containers/tenant-create').then((module) => ({
      default: module.TenantCreate,
    })),
  ),
);

const TenantEdit = Loadable(
  lazy(() =>
    import('../../containers/tenant-edit').then((module) => ({
      default: module.TenantEdit,
    })),
  ),
);

const Users = Loadable(
  lazy(() =>
    import('../../containers/users').then((module) => ({
      default: module.Users,
    })),
  ),
);

const UserCreate = Loadable(
  lazy(() =>
    import('../../containers/user-create').then((module) => ({
      default: module.UserCreate,
    })),
  ),
);

const UserAdd = Loadable(
  lazy(() =>
    import('../../containers/user-add').then((module) => ({
      default: module.UserAdd,
    })),
  ),
);

const UserEdit = Loadable(
  lazy(() =>
    import('../../containers/user-edit').then((module) => ({
      default: module.UserEdit,
    })),
  ),
);

const UsersAll = Loadable(
  lazy(() =>
    import('../../containers/users-all').then((module) => ({
      default: module.UsersAll,
    })),
  ),
);

const UserAdminCreate = Loadable(
  lazy(() =>
    import('../../containers/user-admin-create').then((module) => ({
      default: module.UserAdminCreate,
    })),
  ),
);

const Jobs = Loadable(
  lazy(() =>
    import('../../containers/jobs').then((module) => ({
      default: module.Jobs,
    })),
  ),
);

const InviteCodes = Loadable(
  lazy(() =>
    import('../../containers/invite-codes').then((module) => ({
      default: module.InviteCodes,
    })),
  ),
);

const InviteCodeCreate = Loadable(
  lazy(() =>
    import('../../containers/invite-code-create').then((module) => ({
      default: module.InviteCodeCreate,
    })),
  ),
);

const InviteCode = Loadable(
  lazy(() =>
    import('../../containers/invite-code').then((module) => ({
      default: module.InviteCode,
    })),
  ),
);

const Verticals = Loadable(
  lazy(() =>
    import('../../containers/verticals').then((module) => ({
      default: module.Verticals,
    })),
  ),
);

const VerticalCreate = Loadable(
  lazy(() =>
    import('../../containers/vertical-create').then((module) => ({
      default: module.VerticalCreate,
    })),
  ),
);

const VerticalEdit = Loadable(
  lazy(() =>
    import('../../containers/vertical-edit').then((module) => ({
      default: module.VerticalEdit,
    })),
  ),
);

const Logout = Loadable(
  lazy(() =>
    import('../../containers/logout').then((module) => ({
      default: module.default,
    })),
  ),
);

const routes: PartialRouteObject[] = [
  // {
  //   path: '/',
  //   element: <Navigate to="/" replace />,
  // },
  {
    path: 'token',
    element: (
      <GuestGuard>
        <Token />
      </GuestGuard>
    ),
  },
  {
    path: '500',
    element: (
      <GuestGuard>
        <ErrorPage />
      </GuestGuard>
    ),
  },
  {
    path: 'login',
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  {
    path: 'logout',
    element: (
      <GuestGuard>
        <Logout />
      </GuestGuard>
    ),
  },
  // {
  //   path: 'register',
  //   element: (
  //     <GuestGuard>
  //       <Register />
  //     </GuestGuard>
  //   ),
  // },
  // {
  //   path: 'verify-code',
  //   element: (
  //     <GuestGuard>
  //       <VerifyCode />
  //     </GuestGuard>
  //   ),
  // },
  // {
  //   path: 'password-recovery',
  //   element: (
  //     <GuestGuard>
  //       <PasswordRecovery />
  //     </GuestGuard>
  //   ),
  // },
  // {
  //   path: 'password-reset',
  //   element: <PasswordReset />,
  // },
  {
    path: 'no-permissions',
    element: (
      <AuthGuard>
        <NoPermissions />
      </AuthGuard>
    ),
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      // Users
      {
        path: 'users',
        children: [
          {
            path: '/',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <UsersAll />
              </PermissionsGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <UserAdminCreate />
              </PermissionsGuard>
            ),
          },
        ],
      },
      // Tenants
      {
        path: 'tenants',
        children: [
          {
            path: '/',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <Tenants />
              </PermissionsGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <TenantCreate />
              </PermissionsGuard>
            ),
          },
          {
            path: 'edit/:tenantId',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <TenantEdit />
              </PermissionsGuard>
            ),
          },
          // Individual tenant
          {
            path: ':tenantId',
            element: <TenantGuard />,
            children: [
              {
                path: '/',
                element: <Navigate to="events" replace />,
              },
              // Events
              {
                path: 'events',
                children: [
                  {
                    path: '/',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <Jobs />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':eventId',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <Job />
                      </PermissionsGuard>
                    ),
                  },
                ],
              },
              // Users
              {
                path: 'users',
                children: [
                  {
                    path: '/',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_USERS_READ}>
                        <Users />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: 'create',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_USERS_WRITE}>
                        <UserCreate />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: 'admin',
                    children: [
                      {
                        path: '/',
                        element: (
                          <PermissionsGuard permission={Permission.ADMIN}>
                            <UsersAll />
                          </PermissionsGuard>
                        ),
                      },
                      {
                        path: 'create',
                        element: (
                          <PermissionsGuard permission={Permission.ADMIN}>
                            <UserAdminCreate />
                          </PermissionsGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: ':userId',
                    children: [
                      {
                        path: 'add',
                        element: (
                          <PermissionsGuard permission={Permission.TENANT_USERS_WRITE}>
                            <UserAdd />
                          </PermissionsGuard>
                        ),
                      },
                      {
                        path: 'edit',
                        element: (
                          <PermissionsGuard permission={Permission.TENANT_USERS_WRITE}>
                            <UserEdit />
                          </PermissionsGuard>
                        ),
                      },
                    ],
                  },
                ],
              },
              // Items
              {
                path: 'items',
                children: [
                  {
                    path: '/',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <Items />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':itemId/protect',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_PROTECT}>
                        <ItemProtect />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':itemId/verify',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_VERIFY}>
                        <ItemVerify />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':itemId/job/:jobId',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <ItemJobResult />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':itemId',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <Item />
                      </PermissionsGuard>
                    ),
                    children: [
                      {
                        path: '/',
                        element: <ItemDetails />,
                      },
                      {
                        path: 'timeline',
                        element: <ItemTimeline />,
                      },
                    ],
                  },
                ],
              },
              // SKUs
              {
                path: 'skus',
                children: [
                  {
                    path: '/',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <SKUs />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: 'create',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_WRITE}>
                        <SKUCreate />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':skuId/register',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_PROTECT}>
                        <SKUItemRegister />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':skuId/identify',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_VERIFY}>
                        <SKUItemIdentify />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':skuId/job/:jobId',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <SKUJobResult />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':skuId',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_ITEMS_READ}>
                        <SKU />
                      </PermissionsGuard>
                    ),
                    children: [
                      {
                        path: '/',
                        element: <SKUDetails />,
                      },
                      {
                        path: 'timeline',
                        element: <SKUTimeline />,
                      },
                      {
                        path: 'items',
                        element: <SKUItems />,
                      },
                    ],
                  },
                ],
              },
              // Invite codes
              {
                path: 'invites',
                children: [
                  {
                    path: '/',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_USERS_WRITE}>
                        <InviteCodes />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: 'create',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_USERS_WRITE}>
                        <InviteCodeCreate />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: ':code',
                    element: (
                      <PermissionsGuard permission={Permission.TENANT_USERS_WRITE}>
                        <InviteCode />
                      </PermissionsGuard>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      // Verticals
      {
        path: 'verticals',
        children: [
          {
            path: '/',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <Verticals />
              </PermissionsGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <VerticalCreate />
              </PermissionsGuard>
            ),
          },
          {
            path: ':verticalId/edit',
            element: (
              <PermissionsGuard permission={Permission.ADMIN}>
                <VerticalEdit />
              </PermissionsGuard>
            ),
          },
        ],
      },
      // Statistics
      {
        path: 'statistics',
        element: (
          <PermissionsGuard permission={Permission.ADMIN}>
            <StatisticsGlobal />
          </PermissionsGuard>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
